import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Star, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompetitionSetup() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(!!competitionId);
  
  // Form states
  const [competitionName, setCompetitionName] = useState("");
  const [poetName, setPoetName] = useState("");
  const [isBarometer, setIsBarometer] = useState(false);
  const [deletePoetId, setDeletePoetId] = useState(null);

  const fetchCompetition = useCallback(async () => {
    if (!competitionId) return;
    try {
      const response = await axios.get(`${API}/competitions/${competitionId}`);
      setCompetition(response.data);
      setCompetitionName(response.data.name);
    } catch (error) {
      console.error("Error fetching competition:", error);
      toast.error("Failed to load competition");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchCompetition();
  }, [fetchCompetition]);

  const createCompetition = async (e) => {
    e.preventDefault();
    if (!competitionName.trim()) {
      toast.error("Please enter a competition name");
      return;
    }
    try {
      const response = await axios.post(`${API}/competitions`, {
        name: competitionName
      });
      toast.success("Competition created!");
      navigate(`/setup/${response.data.id}`);
    } catch (error) {
      console.error("Error creating competition:", error);
      toast.error("Failed to create competition");
    }
  };

  const addPoet = async (e) => {
    e.preventDefault();
    if (!poetName.trim()) {
      toast.error("Please enter a poet name");
      return;
    }
    
    // Check if trying to add barometer when one exists
    if (isBarometer && competition?.poets?.some(p => p.is_barometer)) {
      toast.error("A barometer poet already exists");
      return;
    }

    try {
      await axios.post(`${API}/competitions/${competitionId}/poets`, {
        name: poetName,
        is_barometer: isBarometer
      });
      setPoetName("");
      setIsBarometer(false);
      fetchCompetition();
      toast.success(`${poetName} added!`);
    } catch (error) {
      console.error("Error adding poet:", error);
      toast.error("Failed to add poet");
    }
  };

  const removePoet = async () => {
    if (!deletePoetId) return;
    try {
      await axios.delete(`${API}/competitions/${competitionId}/poets/${deletePoetId}`);
      setDeletePoetId(null);
      fetchCompetition();
      toast.success("Poet removed");
    } catch (error) {
      console.error("Error removing poet:", error);
      toast.error("Failed to remove poet");
    }
  };

  const startCompetition = () => {
    if (!competition?.poets?.length) {
      toast.error("Add at least one poet to start");
      return;
    }
    navigate(`/competition/${competitionId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-heading text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Create new competition form
  if (!competitionId) {
    return (
      <div className="max-w-xl mx-auto" data-testid="create-competition-form">
        <h1 className="font-heading font-bold text-3xl tracking-tight mb-8">
          New Competition
        </h1>
        
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={createCompetition}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="competition-name" className="font-heading">
                    Competition Name
                  </Label>
                  <Input
                    id="competition-name"
                    value={competitionName}
                    onChange={(e) => setCompetitionName(e.target.value)}
                    placeholder="e.g., Monthly Poetry Slam - December 2025"
                    className="mt-2"
                    data-testid="competition-name-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="create-competition-submit">
                  Create Competition
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Setup existing competition
  const regularPoets = competition?.poets?.filter(p => !p.is_barometer) || [];
  const barometer = competition?.poets?.find(p => p.is_barometer);
  const hasRequiredPoets = regularPoets.length >= 3 && barometer; // Minimum 3 poets + barometer

  return (
    <div className="space-y-8" data-testid="competition-setup">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight" data-testid="competition-title">
            {competition?.name}
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-1">
            Setup • {regularPoets.length} poets + {barometer ? "1 barometer" : "0 barometer"}
          </p>
        </div>
        <Button 
          onClick={startCompetition}
          disabled={!hasRequiredPoets}
          data-testid="go-to-competition-btn"
        >
          Go to Competition
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Add Poet Form */}
        <div className="lg:col-span-4">
          <Card data-testid="add-poet-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Add Poet</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={addPoet} className="space-y-4">
                <div>
                  <Label htmlFor="poet-name" className="font-heading">
                    Poet Name
                  </Label>
                  <Input
                    id="poet-name"
                    value={poetName}
                    onChange={(e) => setPoetName(e.target.value)}
                    placeholder="Enter poet name"
                    className="mt-2"
                    data-testid="poet-name-input"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-barometer"
                    checked={isBarometer}
                    onCheckedChange={setIsBarometer}
                    disabled={!!barometer}
                    data-testid="barometer-checkbox"
                  />
                  <Label 
                    htmlFor="is-barometer" 
                    className="font-heading text-sm cursor-pointer"
                  >
                    Barometer Poet
                  </Label>
                </div>
                
                <Button type="submit" className="w-full" data-testid="add-poet-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Poet
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Requirements checklist */}
          <Card className="mt-4" data-testid="requirements-card">
            <CardHeader>
              <CardTitle className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
                Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${regularPoets.length >= 3 ? "bg-green-500" : "bg-gray-200"}`} />
                <span className="font-heading text-sm">
                  At least 3 competing poets ({regularPoets.length} added)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${barometer ? "bg-green-500" : "bg-gray-200"}`} />
                <span className="font-heading text-sm">
                  1 barometer poet ({barometer ? "1" : "0"}/1)
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Poets List */}
        <div className="lg:col-span-8">
          <Card data-testid="poets-list-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                Registered Poets ({competition?.poets?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {competition?.poets?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Barometer first */}
                    {barometer && (
                      <TableRow data-testid="barometer-row">
                        <TableCell>
                          <Star className="w-4 h-4 text-orange-500" />
                        </TableCell>
                        <TableCell className="font-heading font-medium">
                          {barometer.name}
                        </TableCell>
                        <TableCell>
                          <span className="badge badge-barometer">Barometer</span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletePoetId(barometer.id)}
                            data-testid={`delete-poet-${barometer.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                    {/* Regular poets */}
                    {regularPoets.map((poet, index) => (
                      <TableRow key={poet.id} data-testid={`poet-row-${index}`}>
                        <TableCell className="font-mono">{index + 1}</TableCell>
                        <TableCell className="font-heading font-medium">
                          {poet.name}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">Competitor</span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletePoetId(poet.id)}
                            data-testid={`delete-poet-${poet.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-12 text-center text-muted-foreground" data-testid="empty-poets-state">
                  <p className="font-heading">No poets added yet</p>
                  <p className="text-sm mt-1">Add at least 3 poets and 1 barometer to start</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deletePoetId} onOpenChange={() => setDeletePoetId(null)}>
        <AlertDialogContent data-testid="delete-poet-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Poet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the poet from the competition. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removePoet} data-testid="confirm-delete-btn">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
